import { useContext } from 'react';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { NotificationsVisibleContext } from '../context';

type TAlertVariant = 'warning' | 'error' | 'success';

interface IAlert {
  id: string;
  title: string;
  message?: string;
  autoClose?: number;
  withCloseButton?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  loading?: boolean;
  variant: TAlertVariant;
  isAccessible?:boolean;
}

interface IAlertReturn {
  showAlert: (alert: IAlert) => void;
}

const checkAlertVariant = (variant: TAlertVariant): string => {
  const alertVariantColors = {
    error: 'red',
    warning: 'yellow',
    success: 'green',
  };
  return alertVariantColors[variant];
};

const useAlert = (): IAlertReturn => {
  const { setIsOpen } = useContext(NotificationsVisibleContext);
  const [t] = useTranslation('common');

  const showAlert = ({
    id,
    withCloseButton = true,
    onClose = () => typeof setIsOpen === 'function' && setIsOpen(false),
    onOpen = () => typeof setIsOpen === 'function' && setIsOpen(true),
    autoClose = 20000,
    title,
    message,
    loading,
    variant,
    isAccessible=false

  }: IAlert): void => {
    notifications.show({
      id,
      withCloseButton,
      onClose,
      onOpen,
      autoClose,
      title,
      message,
      color: checkAlertVariant(variant),
      loading,
      closeButtonProps: { 'aria-label': `${t('hideNotification')}` },
    });
    if (isAccessible) {
        // Focus the notification element for screen readers
        setTimeout(() => {
          const notificationElement = document.querySelector(`#${id}`) as HTMLElement; // Cast to HTMLElement
          if (notificationElement) {
            notificationElement.setAttribute('role', 'alert');
            notificationElement.setAttribute('aria-live', 'assertive');
            notificationElement.setAttribute('tabindex', '-1');
            notificationElement.focus();
          }
        }, 100); // 
      
    }

  };

  return { showAlert };
};

export default useAlert;
